import { Box, SxProps, Theme, Typography, useTheme } from "@mui/material";
import {
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridEventListener,
  GridFooterContainer,
  GridPagination,
  GridRowHeightParams,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridValidRowModel,
  GridApi,
  GridRowParams,
  GridRowId,
  GridSortModel,
  GridInitialState,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import { DataGrid, DataGridProps } from "components/DataGrid";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useGridSelection } from "hooks/useGridSelection";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useRenderTracker } from "hooks/useRenderTracker";
import React, { useCallback, useMemo, useRef, useDeferredValue } from "react";
import { NoRowsOverlay } from "routes/Customer/Chemicals/Inventory/Facility/NoRowsOverlay";

import { metricsLogger } from "../utils/metricsLogger";
import { OmnisearchColumnFilterMenu } from "./OmnisearchColumnPopover";
import {
  DataDisplayProps,
  OmnisearchDataDisplay,
  OmnisearchDataDisplayProps,
} from "./OmnisearchDataDisplay";
import { OmnisearchErrorDisplay } from "./OmnisearchErrorDisplay";

export type GridApiRef = React.RefObject<GridApi | null>;
type DataGridRef = React.RefObject<HTMLDivElement | null>;

export type OmnisearchDataGridProps<TData, TVariables, TRow extends GridValidRowModel> = Omit<
  OmnisearchDataDisplayProps<TData, TVariables, TRow>,
  "dataDisplay"
> & {
  apiRef?: GridApiRef;
  dataGridRef?: DataGridRef;
  gridSx?: SxProps<Theme>;
  slots?: Partial<GridSlotsComponent>;
  columns: OmnisearchGridColDef<TRow>[];
  noDataMessage?: string | string[];
  /** Button text to display in the grid when there is no data for the default search (must be defined to display the button) */
  noDataButtonText?: string;
  rowHeight?: number | "auto";
  getRowHeight?: (params: GridRowHeightParams) => number | "auto";
  onColumnWidthChange?: GridEventListener<"columnWidthChange">;
  pageSizeOptions?: DataGridProps<TRow>["pageSizeOptions"];
  hideFooter?: DataGridProps<TRow>["hideFooter"];
  autosizeOptions?: DataGridPremiumProps<TRow>["autosizeOptions"];
  /** When true, the omnisearch max width will always be 100%. `commandButtons`
   * may still be used */
  showManageColumns?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails<any>
  ) => void;
  getDetailPanelContent?: (params: GridRowParams<TRow>) => React.ReactNode;
  getDetailPanelHeight?: (params: GridRowParams<TRow>) => "auto" | number;
  getRowId?: (row: TRow) => GridRowId;
  disableRowSelectionOnClick?: boolean;
  checkboxSelection?: boolean;
  /** When true, hides the "select all" checkbox in the header while keeping individual row checkboxes */
  disableSelectAll?: boolean;
  isRowSelectable?: (params: GridRowParams<TRow>) => boolean;
  /**
   * Handler called when selection changed with row data.
   *
   * **Cross-page support**: This handler DOES track selections across multiple pages.
   * Selected rows from other pages are preserved when navigating between pages.
   *
   * **Important considerations**:
   * - Row data for items on other pages will be cached/stale (not refreshed)
   * - When pre-selecting rows via `selectedIds` that aren't on the current page,
   *   those rows won't appear in the callback until you navigate to their page
   * - For current page items, you always get fresh/live object references
   *
   * **When to use this handler**:
   * - Bulk selection features where you need row data
   * - Use cases that don't require initial off-page row selection
   * - When cached data for off-page items is acceptable
   *
   * **When to use `onSelectedIdsChanged` instead**:
   * - When you need to pre-select rows that may be off-page initially
   * - When you need guaranteed fresh data for all selected items
   * - When you want to fetch row data separately based on IDs
   */
  onSelectedRowsChanged?: (rows: TRow[]) => void;
  /** Handler called when selection changed. This can include IDs across pages
   * */
  onSelectedIdsChanged?: (ids: GridRowId[]) => void;
  /** Set grid's selected IDs. This can include IDs across pages. */
  selectedIds?: GridRowId[];
  initialSortModel?: GridSortModel;
  initialState?: GridInitialState;
  /**
   * Function to handle the datagrid's onRowClick event.
   * i.e.
   * ```
   * const handleRowClick = (params: GridRowParams) => {
   *  history.push(`/facility/${params.id}`);
   * };
   */
  onRowClick?: (
    params: GridRowParams<TRow>,
    event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
    details: GridCallbackDetails
  ) => void;
  /** When true, hides the empty overlay during initial data load to prevent flash before loading indicator */
  hideEmptyOverlayWhileLoading?: boolean;
  /**
   * When true, automatically deselect items that are no longer visible after filter changes.
   * Default: false (preserves cross-page/filter selections)
   */
  filterSelectionToVisibleItems?: boolean;
};
export function OmnisearchDataGrid<TData, TVariables, TRow extends GridValidRowModel>(
  props: OmnisearchDataGridProps<TData, TVariables, TRow>
) {
  useRenderTracker(
    "OmnisearchDataGrid",
    {
      columns: props.columns?.length,
      showManageColumns: props.showManageColumns,
    },
    { logger: metricsLogger, logToConsole: false }
  );
  const { columns: columnsInput, showManageColumns } = props;

  // Compute whether to show manage columns based on props only (no hook call needed)
  const shouldShowManageColumns = useMemo(() => {
    if (showManageColumns === undefined)
      return columnsInput.filter((c) => c.type !== "actions").length >= 5;
    return showManageColumns;
  }, [showManageColumns, columnsInput]);

  // Use a ref to hold the latest props to avoid creating new dataDisplay function on every render
  // This prevents OmnisearchDataDisplay from re-rendering just because a new function reference is passed
  const propsRef = useRef(props);
  propsRef.current = props;

  // Memoized dataDisplay callback - uses ref to always access latest props without changing identity
  const dataDisplayCallback = useCallback(
    (dataDisplayProps: DataDisplayProps<TData, TRow>) => (
      <HydratedOmnisearchDataGrid {...propsRef.current} {...dataDisplayProps} />
    ),
    [] // Empty deps - function identity never changes, but always reads latest props via ref
  );

  return (
    <OmnisearchDataDisplay
      {...props}
      shouldShowManageColumns={shouldShowManageColumns}
      dataDisplay={dataDisplayCallback}
    />
  );
}

export type HydratedOmnisearchDataGridProps<
  TData,
  TVariables,
  TRow extends GridValidRowModel,
> = OmnisearchDataGridProps<TData, TVariables, TRow> & DataDisplayProps<TData, TRow>;

/**
 * Comparison function for React.memo to prevent unnecessary re-renders.
 * Only re-render when meaningful props change, not just parent re-renders.
 */
function areHydratedPropsEqual<TData, TVariables, TRow extends GridValidRowModel>(
  prevProps: HydratedOmnisearchDataGridProps<TData, TVariables, TRow>,
  nextProps: HydratedOmnisearchDataGridProps<TData, TVariables, TRow>
): boolean {
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.count === nextProps.count &&
    prevProps.items === nextProps.items &&
    prevProps.omnisearch === nextProps.omnisearch &&
    prevProps.sortModel === nextProps.sortModel &&
    prevProps.columnVisibilityModel === nextProps.columnVisibilityModel &&
    prevProps.paginationModel === nextProps.paginationModel &&
    prevProps.error === nextProps.error &&
    prevProps.data === nextProps.data &&
    prevProps.selectedIds === nextProps.selectedIds
  );
}

/**
 * Custom footer component that displays off-page selection count.
 * Extracted as standalone component to prevent footer unmount/remount when selection changes.
 */
const CustomFooter = React.memo(function CustomFooter({
  offPageSelectionCount,
}: {
  offPageSelectionCount: number;
}) {
  return (
    <GridFooterContainer>
      {offPageSelectionCount > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", pl: 2 }}>
          <Typography variant="body2" color="text.secondary">
            +{offPageSelectionCount} selected on other pages
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      <GridPagination />
    </GridFooterContainer>
  );
});

/**
 * This component is used to render the grid with the data already hydrated.
 * Wrapped in React.memo to prevent re-renders when parent updates don't affect grid state.
 */
function HydratedOmnisearchDataGridInner<TData, TVariables, TRow extends GridValidRowModel>(
  props: HydratedOmnisearchDataGridProps<TData, TVariables, TRow>
) {
  // Track key props that could trigger re-renders
  useRenderTracker(
    "HydratedOmnisearchDataGrid",
    {
      loading: props.loading,
      rowCount: props.count,
      itemsLength: props.items?.length,
      omnisearch: props.omnisearch,
      sortModel: props.sortModel,
    },
    { logger: metricsLogger, logToConsole: false }
  );

  const {
    apiRef,
    dataGridRef,
    columns: columnsInput,
    rowHeight,
    getRowHeight,
    gridSx,
    slots,
    noDataMessage = "No results found.",
    noDataButtonText,
    noDataOnButtonClick,
    onSelectedRowsChanged,
    onSelectedIdsChanged,
    isRowSelectable,
    onRowClick,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    initialState,
    getDetailPanelContent,
    getDetailPanelHeight,
    getRowId,
    disableRowSelectionOnClick,
    checkboxSelection = false,
    disableSelectAll = false,
    getRowLink,
    selectedIds,
    pageSizeOptions,
    hideFooter,
    autosizeOptions,
    hideEmptyOverlayWhileLoading = false,
    filterSelectionToVisibleItems = false,
    // Data from query
    error,
    data,
    items,
    count,
    loading,
    previousData,
    paginationModel,
    setPaginationModel,
    // Grid state from OmnisearchDataDisplay (single source of truth)
    columns: processedColumns,
    omnisearch,
    sortModel,
    setOmnisearchImmediate,
    setSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
  } = props;

  const theme = useTheme();
  const { user } = useCurrentUser();

  // Defer items rendering to allow other concurrent updates (like chart queries) to proceed first
  // This prevents the DataGrid from blocking the main thread during large renders
  const deferredItems = useDeferredValue(items);
  const isItemsStale = deferredItems !== items;

  const { rowSelectionModel, setRowSelectionModel, offPageSelectionCount } = useGridSelection<TRow>(
    {
      selectedIds,
      items: deferredItems,
      onSelectedRowsChanged,
      onSelectedIdsChanged,
      filterSelectionToVisibleItems,
      searchString: omnisearch,
    }
  );

  const clearOmnisearch = useCallback(() => {
    // Use immediate for programmatic clear (no debounce)
    setOmnisearchImmediate("");
  }, [setOmnisearchImmediate]);

  // Memoize getRowHeight to prevent new function reference on every render
  const memoizedGetRowHeight = useMemo(
    () => getRowHeight ?? (() => rowHeight ?? 38),
    [getRowHeight, rowHeight]
  );

  // Memoize onRowSelectionModelChange handler
  const handleRowSelectionModelChange = useCallback(
    (newModel: GridRowSelectionModel) => {
      setRowSelectionModel(newModel);
    },
    [setRowSelectionModel]
  );

  // Memoize isRowSelectable wrapper to avoid creating new function every render
  const memoizedIsRowSelectable = useMemo(
    () =>
      isRowSelectable
        ? (params: GridRowParams<TRow>) => !!params.row && isRowSelectable(params)
        : undefined,
    [isRowSelectable]
  );

  // Replace the error throw with graceful handling
  const errorMessage = useMemo(() => {
    if (error) {
      return Array.isArray(error.message)
        ? error.message.join(". ")
        : error.message || "An error occurred while fetching data";
    }
    return null;
  }, [error]);

  const noRowsMessage = useMemo(() => {
    if (errorMessage) return errorMessage;
    return omnisearch ? "No results found." : noDataMessage;
  }, [noDataMessage, omnisearch, errorMessage]);

  const noRowsButtonText = useMemo(
    () => (omnisearch ? "Clear search" : noDataButtonText),
    [noDataButtonText, omnisearch]
  );

  const noRowsOnButtonClick = useMemo(
    () => (omnisearch ? clearOmnisearch : noDataOnButtonClick),
    [clearOmnisearch, noDataOnButtonClick, omnisearch]
  );

  const resetColumnWidth = useCallback(
    (field: string) => {
      const colDef = columnsInput.find((c) => c.field === field);

      if (!colDef) return;

      unsetColumnWidthModel(colDef.field);
    },
    [columnsInput, unsetColumnWidthModel]
  );

  const handleToggleColumn = useCallback(
    (field: string) => {
      const newModel = {
        ...columnVisibilityModel,
        [field]: field in columnVisibilityModel ? !columnVisibilityModel[field] : false,
      };

      if (!newModel[field]) resetColumnWidth(field);

      setColumnVisibilityModel(newModel);

      if (onColumnVisibilityModelChange && apiRef?.current)
        onColumnVisibilityModelChange(newModel, {
          reason: "columnVisibilityChanged",
          api: apiRef?.current,
        });
    },
    [
      onColumnVisibilityModelChange,
      setColumnVisibilityModel,
      resetColumnWidth,
      columnVisibilityModel,
      apiRef,
    ]
  );

  const columnVisibilityModelChanged = useCallback(
    (model: GridColumnVisibilityModel, details: GridCallbackDetails) => {
      setColumnVisibilityModel(model);

      Object.entries(model).forEach(([field, visible]) => {
        if (!visible) resetColumnWidth(field);
      });

      if (onColumnVisibilityModelChange) onColumnVisibilityModelChange(model, details);
    },
    [onColumnVisibilityModelChange, setColumnVisibilityModel, resetColumnWidth]
  );

  const columnWidthChanged: GridEventListener<"columnWidthChange"> = useCallback(
    (params, event, details) => {
      setColumnWidthModel(params);

      if (onColumnWidthChange) onColumnWidthChange(params, event, details);
    },
    [onColumnWidthChange, setColumnWidthModel]
  );

  // Stable columns management slot - extracted for stable reference
  const columnsManagementSlot = useCallback(
    () => (
      <OmnisearchColumnFilterMenu
        columns={processedColumns.filter(
          (column) => column.headerName !== "actions" && column.field !== "actions"
        )}
        handleToggleColumn={handleToggleColumn}
        selectedColumns={columnVisibilityModel}
        setSelectedColumns={(model) => {
          setColumnVisibilityModel(model);
        }}
      />
    ),
    [processedColumns, handleToggleColumn, columnVisibilityModel, setColumnVisibilityModel]
  );

  // Memoized overlay component for stable reference
  const OverlayComponent = useMemo(() => {
    // Extract custom noRowsOverlay from slots to wrap it
    const CustomNoRowsOverlay = slots?.noRowsOverlay;

    // Return a component that handles the overlay logic
    return function OmnisearchOverlay(props: any) {
      // During initial load (no data yet), don't show empty overlay
      // This prevents the flash before loading indicator appears
      if (hideEmptyOverlayWhileLoading && loading && data === undefined) {
        return null;
      }

      if (error) {
        return (
          <OmnisearchErrorDisplay
            error={error}
            onClear={() => {
              clearOmnisearch();
              setSortModel([]);
            }}
          />
        );
      }

      // Use custom overlay if provided
      if (CustomNoRowsOverlay != null) {
        const Overlay = CustomNoRowsOverlay;
        return <Overlay {...props} />;
      }

      return (
        <NoRowsOverlay
          {...props}
          onButtonClick={noRowsOnButtonClick}
          message={noRowsMessage}
          buttonText={noRowsButtonText}
        />
      );
    };
  }, [
    slots?.noRowsOverlay,
    noRowsOnButtonClick,
    noRowsMessage,
    noRowsButtonText,
    error,
    clearOmnisearch,
    setSortModel,
    hideEmptyOverlayWhileLoading,
    loading,
    data,
  ]);

  // Determine if we need our custom footer with off-page selection count
  // Only use custom footer when: selection is enabled, footer isn't hidden, and no custom footer slot provided
  const hasSelectionEnabled =
    !!onSelectedRowsChanged || !!onSelectedIdsChanged || checkboxSelection;
  const shouldUseCustomFooter = hasSelectionEnabled && !hideFooter && !slots?.footer;

  // Stable footer slot - only changes when offPageSelectionCount changes
  // Extracted separately to prevent footer unmount/remount when other slots change
  const footerSlot = useCallback(
    () => <CustomFooter offPageSelectionCount={offPageSelectionCount} />,
    [offPageSelectionCount]
  );

  const memoizedSlots: Partial<GridSlotsComponent> | undefined = useMemo(() => {
    return {
      columnsManagement: columnsManagementSlot,
      noRowsOverlay: OverlayComponent,
      noResultsOverlay: OverlayComponent,
      // Only include custom footer when selection is enabled, footer isn't hidden, and no custom slot provided
      ...(shouldUseCustomFooter && { footer: footerSlot }),
      // Spread other slots but exclude noRowsOverlay and noResultsOverlay since we handle them above
      ...Object.fromEntries(
        Object.entries(slots ?? {}).filter(
          ([key]) => key !== "noRowsOverlay" && key !== "noResultsOverlay"
        )
      ),
    };
  }, [slots, columnsManagementSlot, OverlayComponent, footerSlot, shouldUseCustomFooter]);

  // Memoize the sx prop to avoid creating new object on every render
  // Use deferredItems for sizing calculations
  const memoizedSx = useMemo(
    () => ({
      scrollMarginTop: 10,
      contain: "layout paint",
      "& .MuiDataGrid-virtualScroller": {
        minHeight: deferredItems.length === 0 || error ? "400px" : "0",
      },
      // Show dimmed opacity when loading OR when items are stale (deferred update pending)
      opacity: (loading && previousData) || isItemsStale ? theme.palette.action.disabledOpacity : 1,
      ...(disableSelectAll && {
        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
          display: "none",
        },
      }),
      ...gridSx,
    }),
    [
      deferredItems.length,
      error,
      loading,
      previousData,
      isItemsStale,
      disableSelectAll,
      gridSx,
      theme.palette.action.disabledOpacity,
    ]
  );

  if (!user) return <></>;
  return (
    <DataGrid
      apiRef={apiRef}
      innerRef={dataGridRef}
      columns={processedColumns}
      autosizeOptions={autosizeOptions}
      rows={deferredItems}
      // Defaulted to match our current designs but allows it to be overridden
      getRowHeight={memoizedGetRowHeight}
      autoHeight={rowHeight === "auto"}
      getRowId={getRowId}
      sx={memoizedSx}
      checkboxSelection={!!onSelectedRowsChanged || !!onSelectedIdsChanged || checkboxSelection}
      disableRowSelectionOnClick={
        typeof disableRowSelectionOnClick === "boolean"
          ? disableRowSelectionOnClick
          : !(onSelectedRowsChanged || onSelectedIdsChanged)
      }
      loading={loading || isItemsStale}
      rowCount={count}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      isRowSelectable={memoizedIsRowSelectable}
      onColumnVisibilityModelChange={columnVisibilityModelChanged}
      onColumnWidthChange={columnWidthChanged}
      keepNonExistentRowsSelected
      slots={memoizedSlots}
      onRowClick={onRowClick}
      columnVisibilityModel={columnVisibilityModel}
      initialState={initialState}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      hideFooterPagination={false}
      hideFooter={hideFooter}
      getRowLink={getRowLink}
      pageSizeOptions={pageSizeOptions}
    />
  );
}

/**
 * Memoized version of HydratedOmnisearchDataGrid.
 * Uses custom comparison to only re-render when meaningful props change.
 */
export const HydratedOmnisearchDataGrid = React.memo(
  HydratedOmnisearchDataGridInner,
  areHydratedPropsEqual
) as <TData, TVariables, TRow extends GridValidRowModel>(
  props: HydratedOmnisearchDataGridProps<TData, TVariables, TRow>
) => React.ReactElement | null;
